<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useDisplay } from 'vuetify'
import LeaderboardRulesTooltip from '@/components/leaderboard-rules-tooltip/LeaderboardRulesTooltip.vue'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const userStore = useUserStore()
const { mobile } = useDisplay()
const { t } = useI18n()

const isLoading = ref(false)

const goBack = () => {
  router.back()
}

const fetchLeaderboard = async () => {
  isLoading.value = true
  await userStore.getReferralLeaderboard()
  isLoading.value = false
}

const leaderboard = computed(() => userStore.leaderboard)
const currentUserIndex = computed(() => userStore.currentUserIndex)

const top1User = computed(() => leaderboard.value[0] || null)
const top1Points = computed(() => top1User.value?.referralCount ?? 0)
const myPoints = computed(() => userStore.user?.referralCount ?? 0)

const myDisplayName = computed(() => {
  if (userStore.user?.isShowNamePublic) {
    return userStore.user.firstName || t('leaderboard.youChip')
  }
  const id = userStore.user?.id || ''
  return id.length > 6 ? id.slice(-6) : id
})

const remainingPointsToWin = computed(() => {
  if (currentUserIndex.value === 0) return 0
  if (!top1User.value) return 1
  return top1Points.value + 1 - myPoints.value
})

const totalReferrals = computed(() => userStore.totalReferrals)

const winProbability = computed(() => {
  if (totalReferrals.value === 0) return 0
  return (myPoints.value / totalReferrals.value) * 100
})

const winProbabilityPct = computed(() => {
  const prob = winProbability.value
  if (prob > 5) return Math.min(prob, 100)
  return 5
})

const winProbabilityText = computed(() => {
  const prob = winProbability.value
  if (prob > 5) return `${Math.ceil(prob)}%`
  return t('leaderboard.approx5Pct')
})

const getRankClass = (index: number) => {
  if (index === 0) return 'text-amber font-weight-bold text-body-1'
  if (index === 1) return 'text-grey-lighten-1 font-weight-bold text-body-1'
  if (index === 2) return 'text-brown-darken-1 font-weight-bold text-body-1'
  return 'text-medium-emphasis text-body-2'
}

onMounted(async () => {
  if (!userStore.user?.id) {
    await userStore.getUserData()
  }
  await fetchLeaderboard()
})
</script>

<template>
  <v-container fluid class="d-flex flex-column fill-height ga-4" :class="mobile ? 'pa-4' : 'py-4 px-0'">
    <!-- Шапка страницы -->
    <div class="d-flex align-center w-100">
      <v-btn
        variant="text"
        size="small"
        prepend-icon="ri-arrow-left-line"
        @click="goBack"
      >
        {{ t('common.back') }}
      </v-btn>
      <div class="flex-grow-1 text-center d-flex align-center justify-center ga-1">
        <span class="text-h4">{{ t('leaderboard.leaderboardTitle') }}</span>
        <LeaderboardRulesTooltip :icon-size="20" />
      </div>
    </div>

    <!-- Загрузчик -->
    <div v-if="isLoading" class="d-flex justify-center align-center flex-grow-1">
      <v-progress-circular indeterminate color="primary" size="48" />
    </div>

    <!-- Основной контент -->
    <template v-else>
      <!-- Пьедестал (ТОП-1) -->
      <div v-if="top1User" class="d-flex flex-column align-center ga-2">
        <div class="podium-crown d-flex align-center justify-center rounded-circle">
          <v-icon icon="ri-vip-crown-fill" size="42" color="amber" />
        </div>
        <div class="text-h5 font-weight-bold text-amber">
          {{ top1User.displayName }}
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ t('leaderboard.invitedCount', { count: top1User.referralCount }) }}
        </div>
      </div>

      <!-- Пустое состояние -->
      <div v-if="!leaderboard.length" class="d-flex flex-column align-center justify-center flex-grow-1 text-center ga-2">
        <v-icon icon="ri-trophy-line" size="48" color="secondary" />
        <span class="text-body-1 text-medium-emphasis font-weight-medium">{{ t('leaderboard.emptyStateTitle') }}</span>
        <span class="text-caption text-disabled">{{ t('leaderboard.emptyStateDesc') }}</span>
      </div>

      <!-- Список лидеров -->
      <div v-else class="flex-grow-1 w-100 overflow-y-auto">
        <div class="d-flex align-center py-2 text-caption text-disabled font-weight-bold border-b-sm px-4">
          <span style="width: 70px;">{{ t('leaderboard.colRank') }}</span>
          <span class="flex-grow-1">{{ t('leaderboard.colMember') }}</span>
          <span style="width: 120px;" class="text-right">{{ t('leaderboard.colInvited') }}</span>
        </div>

        <div class="d-flex flex-column">
          <template v-for="(entry, index) in leaderboard" :key="entry.displayName">
            <div
              :class="{ 'user-row-success': index === currentUserIndex }"
              class="d-flex align-center py-3 px-4 border-b-sm"
            >
              <div class="d-flex align-center ga-1 font-weight-bold" style="width: 70px;">
                <span :class="getRankClass(index)">{{ index + 1 }}</span>
                <v-icon v-if="index === 0" icon="ri-vip-crown-fill" color="amber" size="16" />
                <v-icon v-else-if="index === 1" icon="ri-vip-crown-fill" color="grey-lighten-1" size="16" />
                <v-icon v-else-if="index === 2" icon="ri-vip-crown-fill" color="brown-darken-1" size="16" />
              </div>

              <div class="font-weight-medium text-body-1 d-inline-flex align-center ga-2 flex-grow-1">
                {{ entry.displayName }}
                <v-chip v-if="index === currentUserIndex" color="success" size="x-small" class="font-weight-bold" variant="flat">
                  {{ t('leaderboard.youChip') }}
                </v-chip>
              </div>

              <div class="font-weight-bold text-body-1 text-right" style="width: 120px;">
                {{ entry.referralCount }}
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Строка текущего пользователя -->
      <div class="w-100 border-t-sm d-flex flex-column ga-3">
        <div class="d-flex align-center justify-space-between w-100">
          <div class="text-body-1 font-weight-medium text-medium-emphasis d-inline-flex align-center ga-2" :class="mobile ? 'py-2' : 'pt-2'">
            <span v-if="currentUserIndex !== -1">
              {{ t('leaderboard.youRank', { rank: currentUserIndex + 1 }) }}
            </span>
            <span v-else>{{ t('leaderboard.youNotInRank') }}</span>
            <span class="text-caption text-disabled">({{ myDisplayName }})</span>
          </div>
          <span v-if="currentUserIndex !== -1" class="text-body-1 font-weight-bold text-success">
            {{ t('leaderboard.invitedCount', { count: myPoints }) }}
          </span>
        </div>
        
        <!-- Прогресс-бар вероятности выигрыша -->
        <div v-if="currentUserIndex !== -1" class="d-flex flex-column ga-1 w-100">
          <div class="d-flex justify-space-between text-caption text-medium-emphasis">
            <span>{{ t('leaderboard.winProbability') }}</span>
            <span class="font-weight-bold text-success">{{ winProbabilityText }}</span>
          </div>
          <div class="progress-bar-track w-100 rounded overflow-hidden">
            <div 
              class="progress-bar-fill h-100 bg-success rounded" 
              :style="{ width: `${winProbabilityPct}%` }"
            ></div>
          </div>
        </div>
        
        <!-- Подсказка -->
        <div class="text-caption text-left font-weight-medium text-medium-emphasis">
          <template v-if="currentUserIndex !== -1">
            <span v-if="currentUserIndex > 0">
              {{ t('leaderboard.toFirstPlaceRemaining', { count: remainingPointsToWin }) }}
            </span>
            <span v-else class="text-success font-weight-bold">
              {{ t('leaderboard.youLead') }}
            </span>
          </template>
          <template v-else>
            <span>
              {{ t('leaderboard.checkRulesPre') }}
              <LeaderboardRulesTooltip>
                <template #activator="{ props: rulesProps }">
                  <span v-bind="rulesProps" class="text-info text-decoration-underline cursor-pointer font-weight-bold">
                    {{ t('leaderboard.contestRules') }}
                  </span>
                </template>
              </LeaderboardRulesTooltip>
              {{ t('leaderboard.checkRulesPost') }}
            </span>
          </template>
        </div>
      </div>
    </template>
  </v-container>
</template>

<style scoped>
.progress-bar-track {
  height: 6px;
  background-color: rgba(255, 255, 255, 0.1);
}

.progress-bar-fill {
  transition: width 0.3s ease;
}

.podium-crown {
  width: 72px;
  height: 72px;
  background: radial-gradient(circle, rgba(255,193,7,0.15) 0%, rgba(255,193,7,0) 70%);
  border: 2px solid rgba(255, 193, 7, 0.4);
  box-shadow: 0 0 15px rgba(255, 193, 7, 0.2);
}

.user-row-success {
  background-color: rgba(var(--v-theme-success), 0.08) !important;
}

.user-row-success :deep(.v-list-item-title) {
  color: rgb(var(--v-theme-success)) !important;
  font-weight: 700 !important;
}
</style>
